import { useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardHeader,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Tab,
  Typography,
  Link,
} from "@material-ui/core";
import PerfectScrollbar from "react-perfect-scrollbar";
import { TabContext, TabList, TabPanel, Skeleton } from "@material-ui/lab";

import FileCopyIcon from "@mui/icons-material/FileCopy";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import PhoneText from "components/_refactor/text/phone";
import EmailText from "components/_refactor/text/email";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import NotFound from "components/Texts/NotFound";
import ImagePlaceholder from "components/Image/ImagePlaceholder";
import DialogRetirada from "./DialogRetirada";
import DialogConfirmacao from "./DialogConfirmacao";
import DialogRegistroProblema from "./DialogRegistroProblema";

import MaskAndValid from "utils/maskAndValid";
import UtilsFncs from "utils/UtilsFncs.js";
import Api from "lib/api/httpRequestHelper";
import { IsMobile, IsSmall } from "utils/is-mobile";

const useStyles = makeStyles({
  image: {
    width: 128,
    height: 128,
  },
  img: {
    margin: "auto",
    display: "block",
    maxWidth: "100%",
    maxHeight: "100%",
  },
});

const CardResultadoRetireNaLoja = ({
  dataOrder,
  dataTable,
  statusConsulta,
  autoSearch,
  setPedidoSelecionado,
  setRefreshPage,
}) => {
  const classes = { ...useStyles() };
  const snackbar = useSnackbar();
  const [pedidoRegistroProblema, setPedidoRegistroProblema] = useState(null);
  const [confirmarRecebimentoPedido, setConfirmarRecebimentoPedido] =
    useState(null);
  const [openRetirada, setOpenRetirada] = useState(false);
  const [pedidoTab, setPedidoTab] = useState("1");
  const [sugestoesPedidos, setSugestoesPedidos] = useState({});

  const isMobile1200 = IsSmall();
  const isMobile = IsMobile();
  const itemXS = isMobile ? 12 : isMobile1200 ? 6 : 3;

  const toggleModalRetirada = useCallback(() => {
    setOpenRetirada(!openRetirada);
  }, [openRetirada]);

  const handleTabChange = (e, newTab) => {
    setPedidoTab(newTab);
  };

  const onSuggestionsClick = useCallback(async () => {
    if (sugestoesPedidos[dataOrder.IdPedido]) {
      return;
    } else {
      return await Api.get(
        "list-suggestions-products-by-order",
        "product",
        "?idPedido=" + dataOrder.IdPedido,
        true
      )
        .then(({ Success, Result }) => {
          if (Success) {
            setSugestoesPedidos({
              ...sugestoesPedidos,
              [dataOrder.IdPedido]: Result,
            });
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });        });
    }
  }, [dataOrder.IdPedido, snackbar, sugestoesPedidos]);

  useEffect(() => {
    if (pedidoTab === "2") {
      onSuggestionsClick();
    }
  }, [onSuggestionsClick, pedidoTab]);

  return (
    <Card>
      {autoSearch ||
        (dataOrder.Status && (
          <>
            <CardHeader
              title={
                <Grid>
                  <Link
                    color="primary"
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setPedidoSelecionado(null);
                    }}
                  >
                    <ArrowBackIcon fontSize="small" />
                    VOLTAR
                  </Link>
                  <Grid
                    container
                    component={Box}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item xs="auto">
                      <Box
                        component={Typography}
                        variant="h2"
                        marginBottom="0!important"
                      >
                        <Box component="span">Pedido {dataOrder.IdPedido}</Box>
                      </Box>
                    </Grid>
                    <Grid
                      item
                      xs="auto"
                      style={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile && "flex-start",
                      }}
                    >
                      {dataOrder.Status !== "Retirado" &&
                        dataOrder.StatusProblema !== "Com Problema" && (
                          <>
                            {statusConsulta === "P" && (
                              <Box
                                justifyContent="flex-end"
                                display="flex"
                                flexWrap="wrap"
                                style={{
                                  marginRight: ".5rem",
                                  marginBottom: isMobile && ".6rem",
                                }}
                              >
                                <Button
                                  color="default"
                                  variant="contained"
                                  size="small"
                                  onClick={() =>
                                    setPedidoRegistroProblema(
                                      dataOrder.IdPedido
                                    )
                                  }
                                >
                                  Registrar Problema
                                </Button>
                              </Box>
                            )}
                            {dataOrder.TransporteDedicado &&
                              dataOrder.StatusRecebimento !== "Recebido" && (
                                <Box
                                  justifyContent="flex-end"
                                  display="flex"
                                  flexWrap="wrap"
                                  style={{
                                    marginRight: ".5rem",
                                    marginBottom: isMobile && ".6rem",
                                  }}
                                >
                                  <Button
                                    color="default"
                                    variant="contained"
                                    size="small"
                                    onClick={() =>
                                      setConfirmarRecebimentoPedido(
                                        dataOrder.IdPedido
                                      )
                                    }
                                  >
                                    Confirmar recebimento
                                  </Button>
                                </Box>
                              )}
                            <Box
                              justifyContent="flex-end"
                              display="flex"
                              flexWrap="wrap"
                              style={{
                                marginRight: ".5rem",
                                marginBottom: isMobile && ".6rem",
                              }}
                            >
                              <Button
                                color="default"
                                variant="contained"
                                size="small"
                                onClick={toggleModalRetirada}
                              >
                                Registrar Retirada
                              </Button>
                            </Box>
                          </>
                        )}
                    </Grid>
                  </Grid>
                </Grid>
              }
            ></CardHeader>
            <CardContent>
              <Grid container spacing={1}>
                <Grid item xs={itemXS}>
                  <Box textAlign="left">
                    <Typography variant="h3">Dados do Pedido</Typography>
                    <Typography variant="h4">
                      Data:{" "}
                      <Box component="span" fontWeight="300">
                        {MaskAndValid.formattDateBR(dataOrder.DataPedido)}
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      Previsão da Entrega:{" "}
                      <Box component="span" fontWeight="300">
                        {MaskAndValid.formattDateBR(
                          dataOrder.DataPrevisaoEntrega
                        )}
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      Data da Entrega:{" "}
                      <Box component="span" fontWeight="300">
                        {MaskAndValid.formattDateBR(dataOrder.DataEntrega)}
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      Valor Total:{" "}
                      <Box component="span" fontWeight="300">
                        R${MaskAndValid.maskPrice(dataOrder.ValorTotalPedido)}
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      Status e-commerce:{" "}
                      <Box component="span" fontWeight="300">
                        {dataOrder.StatusDescricao}
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      Status Retirada:{" "}
                      <Box component="span" fontWeight="300">
                        {dataOrder.Status}
                      </Box>
                    </Typography>
                    {dataOrder.Apelido && (
                      <Typography variant="h4">
                        Loja de Retirada:{" "}
                        <Box component="span" fontWeight="300">
                          {dataOrder.Apelido}
                        </Box>
                      </Typography>
                    )}

                    {dataOrder.QuantidadeIncremental &&
                      dataOrder.QuantidadeIncremental > 0 && (
                        <Typography
                          variant="h4"
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          Venda Incremental{" "}
                          <CheckIcon
                            style={{
                              height: 20,
                              width: 20,
                              marginLeft: "2px",
                            }}
                          />
                        </Typography>
                      )}
                  </Box>
                </Grid>
                {dataOrder?.ListCotacao?.length > 0 && (
                  <Grid item xs={itemXS}>
                    <Box textAlign="left">
                      <Typography variant="h3"> Pacotes de Entrega </Typography>

                      <div
                        style={{
                          maxHeight: isMobile ? undefined : "275px",
                          overflow: "auto",
                        }}
                      >
                        {dataOrder?.ListCotacao?.map((item, index) => (
                          <>
                            <Typography
                              variant="h4"
                              style={{ marginBottom: 0 }}
                            >
                              Pacote {index + 1}
                            </Typography>
                            <a
                              style={{ fontSize: 12 }}
                              href={item?.UrlRastreio}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {item?.UrlRastreio}
                            </a>
                          </>
                        ))}
                      </div>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={itemXS}>
                  <Box textAlign="left">
                    <Typography variant="h3">Dados do Consumidor</Typography>
                    <Typography variant="h4">
                      Cod. Pedido Consumidor:{" "}
                      <Box component="span" fontWeight="300">
                        {dataOrder.CodPedidoCliente}
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      CPF/CNPJ:{" "}
                      <Box component="span" fontWeight="300" fontSize="0.9rem">
                        {MaskAndValid.maskCPNJCPF(dataOrder.CpfCnpjCliente)}
                        {dataOrder.CpfCnpjCliente && (
                          <IconButton
                            title="Copiar"
                            size="small"
                            onClick={() => {
                              UtilsFncs.CopyToClipboard(
                                dataOrder.CpfCnpjCliente
                              );
                            }}
                          >
                            <FileCopyIcon
                              style={{ marginLeft: 5, marginBottom: 1 }}
                            ></FileCopyIcon>
                          </IconButton>
                        )}
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      Nome:{" "}
                      <Box component="span" fontWeight="300">
                        {dataOrder.NomeCliente}
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      E-mail:{" "}
                      <Box component="span" fontWeight="300">
                        <EmailText email={dataOrder?.EmailCliente} />
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      Telefone:{" "}
                      <Box component="span" fontWeight="300">
                        <PhoneText phone={dataOrder.TelefoneCliente} showCopy />
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      Nome autorizado para retirar:{" "}
                      <Box component="span" fontWeight="300">
                        {dataOrder.NomeAutorizadoRetirar || "-"}
                      </Box>
                    </Typography>
                    <Typography variant="h4">
                      CPF autorizado para retirar:{" "}
                      <Box component="span" fontWeight="300">
                        {dataOrder.CpfAutorizadoRetirar && (
                          <>
                            {MaskAndValid.maskCPNJCPF(
                              dataOrder.CpfAutorizadoRetirar
                            )}
                            <IconButton
                              title="Copiar"
                              size="small"
                              onClick={() => {
                                UtilsFncs.CopyToClipboard(
                                  dataOrder.CpfAutorizadoRetirar
                                );
                              }}
                            >
                              <FileCopyIcon
                                fontSize="small"
                                style={{ marginLeft: 5, marginBottom: 1 }}
                              ></FileCopyIcon>
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Typography>
                  </Box>
                </Grid>
                {dataOrder.TransporteDedicado &&
                  dataOrder.StatusRecebimento === "Recebido" && (
                    <Grid item xs={itemXS}>
                      <Box textAlign="left">
                        <Typography variant="h3">
                          Dados do Recebimento
                        </Typography>
                        <Typography variant="h4">
                          Data:{" "}
                          <Box component="span" fontWeight="300">
                            {MaskAndValid.formattDateBR(
                              dataOrder.DtRecebimento
                            )}
                          </Box>
                        </Typography>
                        <Typography variant="h4">
                          Usuário que recebeu:{" "}
                          <Box component="span" fontWeight="300">
                            {dataOrder.UsuarioRecebimento}
                          </Box>
                        </Typography>
                        <Typography variant="h4">
                          <PerfectScrollbar
                            style={{ maxHeight: 300, overflow: "auto" }}
                          >
                            Observações:{" "}
                            <Box component="span" fontWeight="300">
                              {dataOrder.ObservacaoRecebimento}
                            </Box>
                          </PerfectScrollbar>
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                {dataOrder.Status === "Retirado" && (
                  <Grid item xs={itemXS}>
                    <Box textAlign="left">
                      <Typography variant="h3">Dados da Retirada</Typography>
                      <Typography variant="h4">
                        Data:{" "}
                        <Box component="span" fontWeight="300">
                          {MaskAndValid.formattDateBR(dataOrder.DtRetirada)}
                        </Box>
                      </Typography>
                      <Typography variant="h4">
                        CPF do Retirante:{" "}
                        <Box component="span" fontWeight="300">
                          {MaskAndValid.maskCPNJCPF(dataOrder.CpfRetirante)}
                          <IconButton
                            title="Copiar"
                            size="small"
                            onClick={() => {
                              UtilsFncs.CopyToClipboard(dataOrder.CpfRetirante);
                            }}
                          >
                            <FileCopyIcon
                              fontSize="small"
                              style={{ marginLeft: 5, marginBottom: 1 }}
                            ></FileCopyIcon>
                          </IconButton>
                        </Box>
                      </Typography>
                      <Typography variant="h4">
                        Retirante:{" "}
                        <Box component="span" fontWeight="300">
                          {dataOrder.NomeRetirante}
                        </Box>
                      </Typography>
                      <Typography variant="h4">
                        Atendente:{" "}
                        <Box component="span" fontWeight="300">
                          {dataOrder.NomeAtendente}
                        </Box>
                      </Typography>
                      <Typography variant="h4">
                        Observações:{" "}
                        <Box component="span" fontWeight="300">
                          {dataOrder.Observacoes}
                        </Box>
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {dataOrder.Status === "Com Problema" && (
                  <Grid item xs={itemXS}>
                    <Box textAlign="left">
                      <Typography variant="h3">Dados do Problema</Typography>
                      <Typography variant="h4">
                        Status Problema:{" "}
                        <Box component="span" fontWeight="300">
                          {dataOrder.StatusProblema}
                        </Box>
                      </Typography>
                      <Typography variant="h4">
                        Motivo do Problema:{" "}
                        <Box component="span" fontWeight="300">
                          {dataOrder.MotivoProblema}
                        </Box>
                      </Typography>
                      <Typography variant="h4">
                        <PerfectScrollbar
                          style={{ maxHeight: 300, overflow: "auto" }}
                        >
                          Observações:{" "}
                          <Box component="span" fontWeight="300">
                            {dataOrder.ObservacaoProblema}
                          </Box>
                        </PerfectScrollbar>
                      </Typography>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TabContext value={pedidoTab}>
                    <Box style={{ borderBottom: 1, borderColor: "divider" }}>
                      <TabList
                        onChange={handleTabChange}
                        aria-label="lab API tabs example"
                        variant="scrollable"
                        scrollButtons="on"
                      >
                        <Tab label="Itens" value="1" />
                        <Tab
                          label="Sugestões de produtos para venda adicional"
                          value="2"
                        />
                      </TabList>
                    </Box>
                    <TabPanel value="1">
                      <PerfectScrollbar
                        style={{ maxHeight: 300, overflow: "auto" }}
                      >
                        {dataTable?.map((itemPedido, index) => (
                          <Paper key={index} style={{ padding: "1rem" }}>
                            <Grid container spacing={1}>
                              <Grid item>
                                <ButtonBase className={classes.image}>
                                  <ImagePlaceholder
                                    className={classes.img}
                                    alt="foto do item do pedido"
                                    src={itemPedido.Foto}
                                    width={128}
                                    widthOnError={60}
                                    imagePreviewTitle={`Produto ${itemPedido.IdProduto}`}
                                  />
                                </ButtonBase>
                              </Grid>
                              <Grid item xs={isMobile ? 12 : 6} sm container>
                                <Grid
                                  item
                                  xs={isMobile ? 12 : "auto"}
                                  container
                                  direction="column"
                                  spacing={1}
                                >
                                  <Grid item xs={isMobile ? 12 : "auto"}>
                                    <Typography
                                      gutterBottom
                                      variant="subtitle2"
                                    >
                                      {itemPedido.DescricaoProduto}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      gutterBottom
                                    ></Typography>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      Código do Produto: {itemPedido.IdProduto}
                                    </Typography>
                                  </Grid>
                                </Grid>
                                <Grid item>
                                  <Typography variant="subtitle2">
                                    Quantidade: {itemPedido.QuantidadePedida}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Paper>
                        ))}
                      </PerfectScrollbar>
                    </TabPanel>
                    <TabPanel value="2">
                      <PerfectScrollbar
                        style={{ maxHeight: 300, overflow: "auto" }}
                      >
                        {sugestoesPedidos[dataOrder.IdPedido] ? (
                          sugestoesPedidos[dataOrder.IdPedido].length > 0 ? (
                            sugestoesPedidos[dataOrder.IdPedido]?.map(
                              (sugestao, index) => (
                                <Paper key={index} style={{ padding: "1rem" }}>
                                  <Grid container spacing={1}>
                                    <Grid item>
                                      <ButtonBase className={classes.image}>
                                        <ImagePlaceholder
                                          className={classes.img}
                                          alt="imagem do produto sugerido para venda adicional"
                                          src={sugestao.Url}
                                          width={128}
                                          widthOnError={60}
                                          imagePreviewTitle={`Produto ${sugestao.CodProduto}`}
                                        />
                                      </ButtonBase>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={isMobile ? 12 : 6}
                                      sm
                                      container
                                    >
                                      <Grid
                                        item
                                        xs={isMobile ? 12 : "auto"}
                                        container
                                        direction="column"
                                        spacing={1}
                                      >
                                        <Grid item xs={isMobile ? 12 : "auto"}>
                                          <Typography
                                            gutterBottom
                                            variant="subtitle2"
                                          >
                                            {sugestao.Descricao}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            gutterBottom
                                          ></Typography>
                                          <Typography
                                            variant="body2"
                                            color="textSecondary"
                                          >
                                            Código do Produto:{" "}
                                            {sugestao.CodProduto}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                      <Grid item>
                                        <Typography variant="subtitle2">
                                          Quantidade:{" "}
                                          {sugestao.QuantidadeEstoque}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              )
                            )
                          ) : (
                            <NotFound
                              title="Não foram encontradas sugestões de produtos para o seu pedido :("
                              subtitle=""
                            />
                          )
                        ) : (
                          <>
                            <Paper key={0} style={{ padding: "1rem" }}>
                              <Grid container spacing={1}>
                                <Grid item>
                                  <Skeleton
                                    variant="rect"
                                    width={128}
                                    height={128}
                                  />
                                </Grid>
                                <Grid item xs={6} sm container>
                                  <Grid
                                    item
                                    xs
                                    container
                                    direction="column"
                                    spacing={1}
                                  >
                                    <Grid item xs>
                                      <Typography
                                        gutterBottom
                                        variant="subtitle2"
                                      >
                                        <Skeleton />
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        gutterBottom
                                      ></Typography>
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        <Skeleton />
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Grid item>
                                    <Typography variant="subtitle2">
                                      <Skeleton />
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Paper>
                            <Paper key={1} style={{ padding: "1rem" }}>
                              <Grid container spacing={1}>
                                <Grid item>
                                  <Skeleton
                                    variant="rect"
                                    width={128}
                                    height={128}
                                  />
                                </Grid>
                                <Grid item xs={6} sm container>
                                  <Grid
                                    item
                                    xs
                                    container
                                    direction="column"
                                    spacing={1}
                                  >
                                    <Grid item xs>
                                      <Typography
                                        gutterBottom
                                        variant="subtitle2"
                                      >
                                        <Skeleton />
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        gutterBottom
                                      ></Typography>
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                      >
                                        <Skeleton />
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Grid item>
                                    <Typography variant="subtitle2">
                                      <Skeleton />
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Paper>
                          </>
                        )}
                      </PerfectScrollbar>
                    </TabPanel>
                  </TabContext>
                </Grid>
              </Grid>
            </CardContent>
          </>
        ))}

      <DialogRetirada
        setRefreshPage={setRefreshPage}
        setPedidoSelecionado={setPedidoSelecionado}
        open={openRetirada}
        handleClose={toggleModalRetirada}
        dataOrder={dataOrder}
      />

      <DialogConfirmacao
        setRefreshPage={setRefreshPage}
        setPedidoSelecionado={setPedidoSelecionado}
        open={Boolean(confirmarRecebimentoPedido)}
        idPedido={confirmarRecebimentoPedido}
        dataOrder={dataOrder}
        handleClose={() => setConfirmarRecebimentoPedido(null)}
      />

      {pedidoRegistroProblema && (
        <DialogRegistroProblema
          setRefreshPage={setRefreshPage}
          setPedidoSelecionado={setPedidoSelecionado}
          open={Boolean(pedidoRegistroProblema)}
          idPedido={pedidoRegistroProblema}
          dataOrder={dataOrder}
          handleClose={() => setPedidoRegistroProblema(null)}
        />
      )}
    </Card>
  );
};

CardResultadoRetireNaLoja.propTypes = {
  dataOrder: PropTypes.object,
  dataTable: PropTypes.array,
  statusConsulta: PropTypes.string,
};

export default CardResultadoRetireNaLoja;
